import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { APP_INITIALIZER, ErrorHandler, NgModule, provideZoneChangeDetection } from "@angular/core";
import { provideHttpClient, withJsonpSupport } from "@angular/common/http";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import {
    AccessService,
    AiTsrService,
    AppService,
    AuthGuard,
    AuthUserService,
    CalculationService,
    CommonService,
    ContainerService,
    DeactivateGuard,
    EditorService,
    FontLoaderService,
    LocalStoreService,
    ReversalAuthGuard,
    SceneAddService,
    SceneService,
    StoreSelectorsService,
    StyleService,
    UserService,
} from "./services";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import {
    convertNumberToBoolean,
    HOME_URL,
    isGalaxy,
    isHostFeatures,
    isHostLocal,
    jsDateToEpoch,
    LOGIN_URL,
    TIME_RANGE_SEPARATOR,
    isSkipLoadingDataRoute
} from "./common";
// NOTE: imported from the file, NOT the ./containers barrel. The barrel re-exports editor,
// overview, stations, user, home and start, which would pull the whole app into the shell.
import { NotFoundModule } from "./containers/not-found/not-found.module";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { appReducers } from "./store/reducers/app.reducers";
import { UserEffects } from "./store/effects/user.effects";
import { EffectsModule } from "@ngrx/effects";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
    faDropbox,
    faFacebook,
    faGoogleDrive,
    faGooglePlus,
    faInstagram,
    faLinkedin,
    faPinterest,
    faReddit,
    faTelegram,
    faTiktok,
    faTwitter,
    faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { environment } from "../environments/environment";
import { BehaviorSubject } from "rxjs";
import { MatDialogModule } from "@angular/material/dialog";
import { ConfigEffects } from "./store/effects/config.effects";
import { TwitterService } from "./services/twitter-service";
import { ToastrModule } from "ngx-toastr";
import { EnterpriseService } from "./containers/enterprise/services/enterprise-service";
import { EnterpriseEffects } from "./store/effects/enterprise.effects";
import { ServiceWorkerModule } from "@angular/service-worker";
import { GoogleMap, GoogleMapsModule } from "@angular/google-maps";
import { SocialAuthServiceConfig, SocialLoginModule } from "@abacritt/angularx-social-login";
import { actionAppSettings } from "./store/actions/user.actions";
import { TranslocoRootModule } from "./transloco-root.module";
import { TranslocoService } from "@ngneat/transloco";
import { FasterqEffects } from "./store/effects/fasterq.effects";
import { GlobalErrorHandler } from "./services/global.error.handler-service";
import { FasterqServices } from "./containers/fasterq/services/fasterq-services";
import { OverlayModule } from "@angular/cdk/overlay";
import { StringJSV2Pipe } from "./pipes/stringjs-pipe";
import { PwaPromptService } from "./services/pwa-prompt.service";
import { IAppStatic } from "./interfaces";
import { take } from "rxjs/operators";
import { Lib } from "../libs/Lib";
import "gsap";
import * as Draggable from "gsap/Draggable";
import "gsap/TweenLite";
// Ensure Draggable is loaded (suppress unused warning)
void Draggable;
import * as _ from "lodash";
import { HIGHLIGHT_OPTIONS } from "ngx-highlightjs";

// import {SecureService} from "./services/secure-service";
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from "@angular/material/button-toggle";

/** @default: true
 if set to false manually to false, please remember to set back to true before git submit
 **/

const SHOW_GLOBAL_ERRORS = true;

export function globalErrHandle() {
    if (!SHOW_GLOBAL_ERRORS) return { provide: ErrorHandler, useClass: null };
    if (!isHostLocal()) return { provide: ErrorHandler, useClass: GlobalErrorHandler };
    const timeNow = jsDateToEpoch(new Date());
    const quietUntilStored = Number(window.localStorage.getItem("ignoreErrorsTimes"));
    if (timeNow < quietUntilStored) return { provide: ErrorHandler, useClass: null };
    return { provide: ErrorHandler, useClass: GlobalErrorHandler };
}

let localImports = [
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    TranslocoRootModule,
    GoogleMapsModule,
    MatDialogModule,
    NotFoundModule,
    FontAwesomeModule,
    OverlayModule,
    SocialLoginModule,
    ToastrModule.forRoot({
        enableHtml: true,
        progressBar: true,
        preventDuplicates: !Lib.DevMode(),
    }),
    StoreModule.forRoot(appReducers, {
        runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
            strictStateSerializability: false,
            strictActionSerializability: false,
        },
    }),
    EffectsModule.forRoot([UserEffects, EnterpriseEffects, ConfigEffects, FasterqEffects]),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    ServiceWorkerModule.register("service-worker.web.js", {
        enabled: true,
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: "registerWithDelay:3000",
    }),
];

const initializer = (pwaService: PwaPromptService) => () => {
    // Skip initialization for skipped routes
    if (isSkipLoadingDataRoute()) {
        return Promise.resolve();
    }
    return pwaService.initPwaPrompt();
};

/**
 * Hold bootstrap until the translation file is in, so the first screen is never painted with its
 * labels missing. Without this the layout appears immediately but every `| transloco` resolves to
 * an empty string, and the user stares at blank inputs and unlabelled buttons for seconds - which
 * looks far more broken than simply still loading. The boot splash stays on screen instead.
 *
 * Capped by TRANSLATION_WAIT_MS and it never rejects: if the file is slow or fails outright we go
 * ahead and start anyway, and transloco fills the text in when it eventually arrives. Blocking the
 * whole app on one asset is not worth it.
 */
const TRANSLATION_WAIT_MS = 8000;

/**
 * Pages that render entirely from their own markup: no `| transloco` in the template and no
 * translate() call in the component. Making them WAIT on a ~129KB translation file they will
 * never read just delays the one screen these visitors came for.
 *
 * Note they are not cut off from translations, only un-blocked: the file is still fetched, just
 * in the background after the screen is up. A global surface such as a toast can still translate
 * something, and a raw key like "general.lostNet" on a phone in the field would be its own bug.
 */
const TRANSLATION_UNBLOCKED_ROUTES = ["/shopping-cart/equipment"];

function loadsTranslationsInBackground(): boolean {
    try {
        const path = (window.location.pathname || "").toLowerCase();
        // the equipment prefix also covers /shopping-cart/equipmentpol
        return TRANSLATION_UNBLOCKED_ROUTES.some((route) => path.indexOf(route) > -1);
    } catch (e) {
        return false;
    }
}

const translationsInitializer = (transloco: TranslocoService) => () => {
    if (isSkipLoadingDataRoute()) {
        return Promise.resolve();
    }
    if (loadsTranslationsInBackground()) {
        // start it, do not wait on it - bootstrap continues immediately
        transloco.load(transloco.getActiveLang()).pipe(take(1)).subscribe({error: () => undefined});
        return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
        const done = () => resolve();
        const guard = setTimeout(done, TRANSLATION_WAIT_MS);
        transloco.load(transloco.getActiveLang()).pipe(take(1)).subscribe({
            next: () => { clearTimeout(guard); done(); },
            error: () => { clearTimeout(guard); done(); },
        });
    });
};
const defaultButtonToggleOptions: MatButtonToggleDefaultOptions = {
    hideMultipleSelectionIndicator: true,
    hideSingleSelectionIndicator: true,
};

@NgModule({
    declarations: [AppComponent],
    imports: localImports,
    providers: [
        provideZoneChangeDetection({ eventCoalescing: false, runCoalescing: false }),
        GoogleMap,
        StringJSV2Pipe,
        AuthGuard,
        CalculationService,
        AccessService,
        TwitterService,
        EditorService,
        ContainerService,
        CommonService,
        FontLoaderService,
        UserService,
        LocalStoreService,
        EnterpriseService,
        FasterqServices,
        DeactivateGuard,
        ReversalAuthGuard,
        StyleService,
        SceneAddService,
        AppService,
        StoreSelectorsService,
        AuthUserService,
        SceneService,
        AiTsrService,
        GlobalErrorHandler,
        provideHttpClient(
            withJsonpSupport(),
        ),
        {
            provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
            useValue: defaultButtonToggleOptions,
        },
        globalErrHandle(),
        // SecureService, // enable to test service
        // ChecklistDatabaseService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializer,
            deps: [PwaPromptService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: translationsInitializer,
            deps: [TranslocoService],
            multi: true,
        },
        {
            provide: HOME_URL,
            useValue: "",
        },
        {
            provide: 'SocialAuthServiceConfig',
            useValue: {
                autoLogin: false,
                // Google login runs through GoogleIdentityService (lazy GIS load, no boot time One Tap prompt),
                // Facebook stays disabled, so the library has no providers to boot
                providers: [],
                onError: (err) => {
                    if (isGalaxy()) {
                        console.error(err);
                    }
                },
            } as SocialAuthServiceConfig,
        },
        {
            provide: LOGIN_URL,
            useValue: "login",
        },
        {
            provide: TIME_RANGE_SEPARATOR,
            useValue: " - ",
        },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import("highlight.js/lib/core"),
                lineNumbersLoader: () => import("ngx-highlightjs/line-numbers"),
                languages: {
                    typescript: () => import("highlight.js/lib/languages/typescript"),
                    javascript: () => import("highlight.js/lib/languages/javascript.js"),
                    css: () => import("highlight.js/lib/languages/css"),
                    xml: () => import("highlight.js/lib/languages/xml"),
                },

                // fullLibraryLoader: () => import('highlight.js'),
            },
        },

        // ,{
        //     provide: ErrorHandler,
        //     useClass: GlobalErrorHandler
        // }
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: AuthInterceptor,
        //     multi: true
        // },
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: ErrorResponseInterceptor,
        //     multi: true
        // }
    ],
    exports: [],
    bootstrap: [AppComponent],
})

export class AppModule {
    // force rxjs workaround
    readonly bs: BehaviorSubject<any> = new BehaviorSubject({});

    constructor(private library: FaIconLibrary,
                private globalErrorHandler: GlobalErrorHandler,
                private storeSelector: StoreSelectorsService,
                private localStoreService: LocalStoreService) {

        // Skip all initialization for auth-callback route
        if (isSkipLoadingDataRoute()) {
            return;
        }

        // Add social media brand icons to FontAwesome library
        library.addIcons(
            faDropbox, faFacebook, faGoogleDrive, faGooglePlus, faInstagram,
            faLinkedin, faPinterest, faReddit, faTelegram, faTiktok, faTwitter, faYoutube,
        );

        storeSelector.getAppSettings().pipe(take(1))
            .subscribe((appSettings: IAppStatic) => {
                /** we show all features if utl has /features or if we are running in localhost **/
                const saveFullFeature = convertNumberToBoolean(this.localStoreService.getStorageValConfig("saveFullFeature"));
                if (isHostFeatures() || isHostLocal() || saveFullFeature) {
                    const appData = _.cloneDeep(appSettings);
                    ///////////////////////////////////////////////
                    /** override to value of [false] to see what real customers will see, default should be set to [true] **/
                    appData.isFullFeatures = true;
                    ///////////////////////////////////////////////
                    console.log("> full featured mode");
                    this.storeSelector.dispatch(actionAppSettings({ appData }));
                }
            });
    }
}

// ServiceWorkerModule.register('ngsw-worker.js', {enabled: environment.production, registrationStrategy: 'registerWhenStable:5000'})
// if (Lib.DevMode()) {
//     localImports = localImports.filter((v) => v !== SocialLoginModule);
// }

// overlayContainer.getContainerElement().classList.add('app-theme-dark');
// overlayContainer.getContainerElement().classList.add('app-theme-light');

// // if (isGalaxy() && !isHostFeatures()) return [];
// // if (isHostDev()) return [];
// // if (isHostLocal()) return [];
// if (isHostFeatures()) {
//     console.log('> loaded social module');
//     return [
//         {
//             id: GoogleLoginProvider.PROVIDER_ID,
//             provider: new GoogleLoginProvider("637219265100-r4jh7dppmcs1so7mr1jpvffmskke8mrm.apps.googleusercontent.com")
//         }
//         // ,
//         // {
//         //     id: FacebookLoginProvider.PROVIDER_ID,
//         //     provider: new FacebookLoginProvider('608495059928829')
//         // }
//     ];
// } else {
//     return [];
// }


